import React, { useMemo, useState, useEffect } from "react";
import { Download, Plus, Search, ShieldAlert, Trophy, Users, X } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";
import { Badge, Card, FieldBase, PrimaryButton, SecondaryButton, SelectBase, cx } from "./b2cUi";

const LoyaltyManagement = () => {
  const [loyaltyData, setLoyaltyData] = useState([]);
  const [referralData, setReferralData] = useState([]);
  const [fraudData, setFraudData] = useState([]);
  const [filteredLoyalty, setFilteredLoyalty] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [filteredFraud, setFilteredFraud] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("loyalty");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Mock data - replace with actual API call
  const mockLoyaltyData = [
    {
      id: 1,
      customerName: "John Doe",
      customerEmail: "john@example.com",
      tier: "Gold",
      points: 2500,
      totalSpent: 5000,
      ordersCount: 25,
      joinDate: "2023-01-15",
      lastActivity: "2024-01-15",
      status: "active",
      nextTierPoints: 500,
      lifetimeValue: 5000
    },
    {
      id: 2,
      customerName: "Jane Smith",
      customerEmail: "jane@example.com",
      tier: "Platinum",
      points: 5000,
      totalSpent: 10000,
      ordersCount: 50,
      joinDate: "2022-06-20",
      lastActivity: "2024-01-14",
      status: "active",
      nextTierPoints: 0,
      lifetimeValue: 10000
    },
    {
      id: 3,
      customerName: "Mike Johnson",
      customerEmail: "mike@example.com",
      tier: "Silver",
      points: 800,
      totalSpent: 2000,
      ordersCount: 10,
      joinDate: "2023-08-10",
      lastActivity: "2024-01-10",
      status: "inactive",
      nextTierPoints: 1200,
      lifetimeValue: 2000
    },
    {
      id: 4,
      customerName: "Sarah Wilson",
      customerEmail: "sarah@example.com",
      tier: "Bronze",
      points: 200,
      totalSpent: 500,
      ordersCount: 3,
      joinDate: "2024-01-01",
      lastActivity: "2024-01-12",
      status: "active",
      nextTierPoints: 300,
      lifetimeValue: 500
    }
  ];

  const mockReferralData = [
    {
      id: 1,
      referrerName: "John Doe",
      referrerEmail: "john@example.com",
      referredName: "Alice Brown",
      referredEmail: "alice@example.com",
      referralCode: "JOHN2024",
      status: "completed",
      reward: 50,
      date: "2024-01-10",
      referredOrderValue: 250
    },
    {
      id: 2,
      referrerName: "Jane Smith",
      referrerEmail: "jane@example.com",
      referredName: "Bob Wilson",
      referredEmail: "bob@example.com",
      referralCode: "JANE2024",
      status: "pending",
      reward: 25,
      date: "2024-01-12",
      referredOrderValue: 0
    },
    {
      id: 3,
      referrerName: "Mike Johnson",
      referrerEmail: "mike@example.com",
      referredName: "Carol Davis",
      referredEmail: "carol@example.com",
      referralCode: "MIKE2024",
      status: "completed",
      reward: 75,
      date: "2024-01-08",
      referredOrderValue: 500
    }
  ];

  const mockFraudData = [
    {
      id: 1,
      customerName: "Suspicious User",
      customerEmail: "suspicious@example.com",
      riskScore: 85,
      flaggedReasons: ["Multiple accounts", "Unusual behavior", "High return rate"],
      status: "under_review",
      flaggedDate: "2024-01-15",
      totalOrders: 5,
      totalReturns: 4,
      suspiciousActivity: "Created 3 accounts in 1 day"
    },
    {
      id: 2,
      customerName: "High Risk Customer",
      customerEmail: "risk@example.com",
      riskScore: 92,
      flaggedReasons: ["Chargeback history", "Fake address"],
      status: "blocked",
      flaggedDate: "2024-01-14",
      totalOrders: 2,
      totalReturns: 2,
      suspiciousActivity: "Multiple chargebacks"
    }
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoyaltyData(mockLoyaltyData);
      setReferralData(mockReferralData);
      setFraudData(mockFraudData);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = [];

    if (activeTab === "loyalty") {
      filtered = loyaltyData;
    } else if (activeTab === "referrals") {
      filtered = referralData;
    } else {
      filtered = fraudData;
    }

    // Filter by search term
    if (searchFilter) {
      if (activeTab === "loyalty") {
        filtered = filtered.filter(item =>
          item.customerName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          item.customerEmail.toLowerCase().includes(searchFilter.toLowerCase()) ||
          item.tier.toLowerCase().includes(searchFilter.toLowerCase())
        );
      } else if (activeTab === "referrals") {
        filtered = filtered.filter(item =>
          item.referrerName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          item.referredName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          item.referralCode.toLowerCase().includes(searchFilter.toLowerCase())
        );
      } else {
        filtered = filtered.filter(item =>
          item.customerName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          item.customerEmail.toLowerCase().includes(searchFilter.toLowerCase())
        );
      }
    }

    // Filter by tier (loyalty only)
    if (activeTab === "loyalty" && tierFilter !== "all") {
      filtered = filtered.filter(item => item.tier.toLowerCase() === tierFilter.toLowerCase());
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (activeTab === "loyalty") {
      setFilteredLoyalty(filtered);
    } else if (activeTab === "referrals") {
      setFilteredReferrals(filtered);
    } else {
      setFilteredFraud(filtered);
    }
  }, [loyaltyData, referralData, fraudData, searchFilter, tierFilter, statusFilter, activeTab]);

  const handleCreateItem = () => {
    setShowCreateModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const handleDeleteItem = (itemId) => {
    const itemType = activeTab === "loyalty" ? "loyalty record" : activeTab === "referrals" ? "referral" : "fraud record";
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      if (activeTab === "loyalty") {
        setLoyaltyData(prev => prev.filter(item => item.id !== itemId));
      } else if (activeTab === "referrals") {
        setReferralData(prev => prev.filter(item => item.id !== itemId));
      } else {
        setFraudData(prev => prev.filter(item => item.id !== itemId));
      }
    }
  };

  const tierTone = (tier) => {
    const tierConfig = {
      Bronze: "orange",
      Silver: "slate",
      Gold: "yellow",
      Platinum: "purple",
      Diamond: "blue",
    };
    return tierConfig[tier] || "slate";
  };

  const statusTone = (status) => {
    const statusConfig = {
      active: "green",
      inactive: "rose",
      pending: "yellow",
      completed: "blue",
      under_review: "orange",
      blocked: "rose",
    };
    return statusConfig[status] || "slate";
  };

  const getRiskColor = (score) => {
    if (score >= 80) return "text-rose-700";
    if (score >= 60) return "text-orange-700";
    if (score >= 40) return "text-yellow-700";
    return "text-emerald-700";
  };

  const getRiskBarColor = (score) => {
    if (score >= 80) return "bg-rose-500";
    if (score >= 60) return "bg-orange-500";
    if (score >= 40) return "bg-yellow-500";
    return "bg-emerald-500";
  };

  const totalPoints = useMemo(() => loyaltyData.reduce((sum, item) => sum + item.points, 0), [loyaltyData]);
  const totalReferrals = useMemo(
    () => referralData.filter((item) => item.status === "completed").length,
    [referralData]
  );
  const totalFraudCases = useMemo(
    () => fraudData.filter((item) => item.status === "under_review").length,
    [fraudData]
  );
  const avgLifetimeValue = useMemo(() => {
    if (loyaltyData.length === 0) return 0;
    return loyaltyData.reduce((sum, item) => sum + item.lifetimeValue, 0) / loyaltyData.length;
  }, [loyaltyData]);

  const tabBase =
    "px-3 py-2 rounded-2xl text-sm font-black border transition-colors inline-flex items-center gap-2";
  const tabActive = "bg-white border-slate-200 text-slate-900";
  const tabInactive = "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100";

  return (
    <AdminPageShell
      title="Loyalty & Referrals"
      description="Manage loyalty points, referrals, and fraud detection. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="yellow">
            <Trophy className="h-4 w-4" />
            {totalPoints.toLocaleString()} points
          </Badge>
          <Badge tone="green">
            <Users className="h-4 w-4" />
            {totalReferrals} completed referrals
          </Badge>
          <Badge tone="rose">
            <ShieldAlert className="h-4 w-4" />
            {totalFraudCases} fraud cases
          </Badge>
          <Badge tone="gray">${avgLifetimeValue.toFixed(0)} avg LTV</Badge>
        </div>
      }
      actions={
        <>
          <SecondaryButton
            type="button"
            onClick={() => {
              setSearchFilter("");
              setTierFilter("all");
              setStatusFilter("all");
            }}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleCreateItem}
          >
            <Plus className="h-4 w-4" />
            Create
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Total Points</p>
                <p className="text-xl font-black text-slate-900">{totalPoints.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Successful Referrals</p>
                <p className="text-xl font-black text-slate-900">{totalReferrals}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Fraud Cases</p>
                <p className="text-xl font-black text-slate-900">{totalFraudCases}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <span className="text-sm font-black text-slate-700">$</span>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Avg. Lifetime Value</p>
                <p className="text-xl font-black text-slate-900">${avgLifetimeValue.toFixed(0)}</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("loyalty")}
                className={cx(tabBase, activeTab === "loyalty" ? tabActive : tabInactive)}
              >
                <Trophy className="h-4 w-4" />
                Loyalty Points ({loyaltyData.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("referrals")}
                className={cx(tabBase, activeTab === "referrals" ? tabActive : tabInactive)}
              >
                <Users className="h-4 w-4" />
                Referral Program ({referralData.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("fraud")}
                className={cx(tabBase, activeTab === "fraud" ? tabActive : tabInactive)}
              >
                <ShieldAlert className="h-4 w-4" />
                Fraud Detection ({fraudData.length})
              </button>
            </div>

            <SecondaryButton type="button" onClick={() => alert("Export coming soon")}
            >
              <Download className="h-4 w-4" />
              Export
            </SecondaryButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
            <div className="md:col-span-5">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "loyalty"
                      ? "Search by customer name, email, or tier"
                      : activeTab === "referrals"
                      ? "Search by referrer, referred, or code"
                      : "Search by customer name or email"
                  }
                  className={cx(FieldBase, "pl-10")}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="md:col-span-3">
              {activeTab === "loyalty" ? (
                <>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Tier</label>
                  <select className={SelectBase} value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
                    <option value="all">All Tiers</option>
                    <option value="Bronze">Bronze</option>
                    <option value="Silver">Silver</option>
                    <option value="Gold">Gold</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Diamond">Diamond</option>
                  </select>
                </>
              ) : (
                <div className="h-full" />
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Status</label>
              <select className={SelectBase} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                {activeTab === "loyalty" ? (
                  <>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </>
                ) : null}
                {activeTab === "referrals" ? (
                  <>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </>
                ) : null}
                {activeTab === "fraud" ? (
                  <>
                    <option value="under_review">Under Review</option>
                    <option value="blocked">Blocked</option>
                    <option value="resolved">Resolved</option>
                  </>
                ) : null}
              </select>
            </div>

            <div className="md:col-span-2 flex items-end">
              <PrimaryButton type="button" className="w-full justify-center" onClick={handleCreateItem}
              >
                <Plus className="h-4 w-4" />
                {activeTab === "loyalty" ? "Add Points" : activeTab === "referrals" ? "Create Referral" : "Flag Customer"}
              </PrimaryButton>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
              <p className="mt-2 text-slate-600">Loading data…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {activeTab === "loyalty" ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Tier & Points
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Lifetime Value
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Actions
                        </th>
                      </>
                    ) : activeTab === "referrals" ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Referrer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Referred Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Referral Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Reward
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Actions
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Risk Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Flagged Reasons
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Actions
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                      {(activeTab === "loyalty" ? filteredLoyalty : activeTab === "referrals" ? filteredReferrals : filteredFraud).map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          {activeTab === "loyalty" ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.customerName}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.customerEmail}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Joined: {new Date(item.joinDate).toLocaleDateString()}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Badge tone={tierTone(item.tier)}>{item.tier}</Badge>
                                </div>
                                <div className="text-sm text-slate-900 mt-1">
                                  {item.points.toLocaleString()} points
                                </div>
                                {item.nextTierPoints > 0 && (
                                  <div className="text-xs text-slate-600">
                                    {item.nextTierPoints} to next tier
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {item.ordersCount} orders
                                </div>
                                <div className="text-sm text-slate-600">
                                  ${item.totalSpent.toLocaleString()} spent
                                </div>
                                <div className="text-xs text-slate-500">
                                  Last: {new Date(item.lastActivity).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-slate-900">
                                  ${item.lifetimeValue.toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge tone={statusTone(item.status)}>
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Badge>
                              </td>
                            </>
                          ) : activeTab === "referrals" ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.referrerName}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.referrerEmail}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.referredName}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.referredEmail}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-slate-900">
                                  {item.referralCode}
                                </div>
                                <div className="text-xs text-slate-600">
                                  {new Date(item.date).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-slate-900">
                                  {item.reward} points
                                </div>
                                {item.referredOrderValue > 0 && (
                                  <div className="text-xs text-slate-600">
                                    Order: ${item.referredOrderValue}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge tone={statusTone(item.status)}>
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Badge>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.customerName}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.customerEmail}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Flagged: {new Date(item.flaggedDate).toLocaleDateString()}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`text-sm font-medium ${getRiskColor(item.riskScore)}`}>
                                  {item.riskScore}/100
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                                  <div
                                    className={cx("h-2 rounded-full", getRiskBarColor(item.riskScore))}
                                    style={{ width: `${item.riskScore}%` }}
                                  ></div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {item.flaggedReasons.join(', ')}
                                </div>
                                <div className="text-xs text-slate-600 mt-1">
                                  {item.suspiciousActivity}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {item.totalOrders} orders
                                </div>
                                <div className="text-sm text-slate-600">
                                  {item.totalReturns} returns
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge tone={statusTone(item.status)}>
                                  {item.status.replace('_', ' ').charAt(0).toUpperCase() + item.status.replace('_', ' ').slice(1)}
                                </Badge>
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-wrap items-center gap-2">
                              <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => handleEditItem(item)}>
                                View
                              </SecondaryButton>
                              {activeTab === "fraud" && (
                                <>
                                  <SecondaryButton
                                    type="button"
                                    className="px-3 py-1.5 text-xs border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                  >
                                    Resolve
                                  </SecondaryButton>
                                  <SecondaryButton
                                    type="button"
                                    className="px-3 py-1.5 text-xs border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                                  >
                                    Block
                                  </SecondaryButton>
                                </>
                              )}
                              <SecondaryButton
                                type="button"
                                className="px-3 py-1.5 text-xs border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                Delete
                              </SecondaryButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(activeTab === "loyalty" ? filteredLoyalty : activeTab === "referrals" ? filteredReferrals : filteredFraud).length === 0 && !loading && (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-600">
                    No {activeTab === "loyalty" ? "loyalty records" : activeTab === "referrals" ? "referrals" : "fraud cases"} found matching your criteria.
                  </p>
                </div>
              )}
        </Card>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowCreateModal(false);
              setEditingItem(null);
            }}
          />
          <div className="relative w-full max-w-2xl">
            <Card className="p-0 overflow-hidden shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-base font-black text-slate-900">
                  {editingItem
                    ? `Edit ${activeTab}`
                    : `Create New ${activeTab === "loyalty" ? "Loyalty Record" : activeTab === "referrals" ? "Referral" : "Fraud Case"}`}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingItem(null);
                  }}
                  className="inline-flex items-center justify-center rounded-2xl p-2 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                {activeTab === "loyalty" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Customer Name
                      </label>
                      <input type="text" className={FieldBase} defaultValue={editingItem?.customerName || ""} />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Points
                      </label>
                      <input type="number" className={FieldBase} defaultValue={editingItem?.points || ""} />
                    </div>
                  </div>
                ) : activeTab === "referrals" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Referrer Email
                      </label>
                      <input type="email" className={FieldBase} defaultValue={editingItem?.referrerEmail || ""} />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Referred Email
                      </label>
                      <input type="email" className={FieldBase} defaultValue={editingItem?.referredEmail || ""} />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Customer Email
                      </label>
                      <input type="email" className={FieldBase} defaultValue={editingItem?.customerEmail || ""} />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Risk Score
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className={FieldBase}
                        defaultValue={editingItem?.riskScore || ""}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingItem(null);
                  }}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="button">{editingItem ? "Update" : "Create"}</PrimaryButton>
              </div>
            </Card>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default LoyaltyManagement;
